import { useState } from 'react'
import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { NewRtaDialog } from '@/components/rtas/NewRtaDialog'
import { RtaTable } from '@/components/rtas/RtaTable'
import { Button } from '@/components/ui/button'
import { accountQuery } from '@/lib/queries'
import { rtasQuery } from '@/lib/rta-queries'

/**
 * A client workspace's RTAs. No client picker — the workspace IS the client — and a disabled
 * product redirects to the client's Home, the workspace you're in.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/subaccounts/$subaccountId/rtas')({
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.rta)) {
      throw redirect({
        to: '/app/accounts/$accountId/subaccounts/$subaccountId',
        params: { accountId: params.accountId, subaccountId: params.subaccountId },
      })
    }
    await context.queryClient.ensureQueryData(rtasQuery({ subaccountId: params.subaccountId }))
  },
  component: ClientRtasPage,
})

function ClientRtasPage() {
  const { accountId, subaccountId } = Route.useParams()
  const { data } = useSuspenseQuery(rtasQuery({ subaccountId }))
  const [creating, setCreating] = useState(false)

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="RTAs"
        description="This client's real-time alerts."
        className="mb-0"
        actions={<Button onClick={() => setCreating(true)}>New RTA</Button>}
      />
      {data.rtas.length === 0 ? (
        <p className="text-sm text-muted-foreground">No RTAs for this client yet.</p>
      ) : (
        <RtaTable rtas={data.rtas} showClient={false} />
      )}
      {creating && (
        <NewRtaDialog
          accounts={[{ id: accountId, name: '' }]}
          subaccountId={subaccountId}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  )
}
